import React, { useMemo, useRef } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';

const normalizeLogoSrc = (logoSrc) => {
  if (!logoSrc?.startsWith('data:image/png;base64,')) return logoSrc;

  try {
    const base64 = logoSrc.split(',')[1];
    const decoded = atob(base64.substring(0, 30));
    if (decoded.trimStart().startsWith('<svg') || decoded.trimStart().startsWith('<?xml')) {
      return `data:image/svg+xml;base64,${base64}`;
    }
  } catch {
    return logoSrc;
  }

  return logoSrc;
};

export default function LogoOverlaySection({
  logoSrc,
  onUpload,
  onDelete,
  uploadLabel = 'Upload Logo',
  uploadHint = 'PNG or SVG recommended',
  emptyStateMessage = 'No logo detected.',
  logoOverlayEnabled,
  onLogoOverlayEnabledChange,
  logoOverlayPosition,
  onLogoOverlayPositionChange,
  logoOverlayOpacity,
  onLogoOverlayOpacityChange,
  onLogoOverlayOpacityPreview,
  allowOverlayWithoutLogo = false,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const normalizedLogoSrc = useMemo(() => normalizeLogoSrc(logoSrc), [logoSrc]);
  const canShowOverlayControls = !!normalizedLogoSrc || allowOverlayWithoutLogo;

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      onUpload?.(file);
      event.target.value = '';
    }
  };

  const handleOpacityPreview = (value) => {
    if (onLogoOverlayOpacityPreview) {
      onLogoOverlayOpacityPreview(value);
      return;
    }
    onLogoOverlayOpacityChange?.(value);
  };

  return (
    <div className="space-y-4">
      {onUpload ? (
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/svg+xml,image/jpeg,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
      ) : null}

      {normalizedLogoSrc ? (
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-muted/20 p-2">
              <img src={normalizedLogoSrc} alt="Business logo" className="max-h-full max-w-full object-contain" />
            </div>
            {onUpload || onDelete ? (
              <div className="flex flex-col gap-2">
                {onUpload ? (
                  <Button size="sm" variant="outline" disabled={disabled} onClick={() => inputRef.current?.click()}>
                    Replace
                  </Button>
                ) : null}
                {onDelete ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    className="text-destructive hover:text-destructive"
                    onClick={onDelete}
                  >
                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                    Remove
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : onUpload ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-muted-foreground/25 py-8 text-muted-foreground transition-all hover:border-coral/50 hover:bg-coral/5 hover:text-coral disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Upload className="h-6 w-6" />
          <span className="text-sm font-medium">{uploadLabel}</span>
          <span className="text-xs">{uploadHint}</span>
        </button>
      ) : (
        <p className="text-sm text-muted-foreground">{emptyStateMessage}</p>
      )}

      {canShowOverlayControls ? (
        <div className="space-y-4 rounded-xl border bg-muted/20 p-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Show logo on video</Label>
            <Switch
              checked={logoOverlayEnabled}
              disabled={disabled}
              onCheckedChange={onLogoOverlayEnabledChange}
            />
          </div>
          {logoOverlayEnabled ? (
            <>
              <div className="space-y-2">
                <Label className="text-sm">Position</Label>
                <Select value={logoOverlayPosition} disabled={disabled} onValueChange={onLogoOverlayPositionChange}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top-left">Top Left</SelectItem>
                    <SelectItem value="top-right">Top Right</SelectItem>
                    <SelectItem value="bottom-left">Bottom Left</SelectItem>
                    <SelectItem value="bottom-right">Bottom Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Opacity</Label>
                  <span className="text-sm text-muted-foreground">{Math.round(logoOverlayOpacity * 100)}%</span>
                </div>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  disabled={disabled}
                  value={[Math.round(logoOverlayOpacity * 100)]}
                  onValueChange={([nextValue]) => handleOpacityPreview(nextValue / 100)}
                  onValueCommit={([nextValue]) => onLogoOverlayOpacityChange?.(nextValue / 100)}
                />
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
